import appConfig from "@config/app";
import { UPGRADE_REQUIRED } from "@shared/libs/oh-errors/error-messages";
import { manageTooManyRequestError } from "@shared/libs/oh-errors/manage-too-many-request-error";
import { OhApi } from "@shared/libs/siayuda";
import { ApiResponse } from "@shared/libs/siayuda/oh-api/types";
import {
  CURRENT_LEGAL_MODE_LOCAL_STORAGE_KEY as ENTITY_MODE_STORAGE_KEY,
  CURRENT_LEGAL_ENTITY_LOCAL_STORAGE_KEY as ENTITY_UUID_STORAGE_KEY,
} from "hassibot/current-legal-entity-provider";
import { get } from "hassibot/local-storage";

import { LegalEntityUuid, LegalMode } from "./common/types";
import { getMicrosoftToken, msalInstance } from "./login/msal";

// siayuda does not allow us to have a "logged out" client, we hack it with
// this.
const FAKE_TOKEN = "FAKE_INVALID_TOKEN";

const currentLegalEntityUuid = get(ENTITY_UUID_STORAGE_KEY) as null | LegalEntityUuid;
export const currentLegalMode = get(ENTITY_MODE_STORAGE_KEY) as null | LegalMode;
const auth = new OhApi.AuthConfig("HB", FAKE_TOKEN);

class LegalEntityRestrictedApiClient extends OhApi.ApiV2Client {
  public buildHeaders = (additionalHeaders?: Record<string, string>): Headers =>
    new Headers({
      ...(this.config.auth && this.config.auth.makeAuthHeader()),
      ...(currentLegalEntityUuid && { "X-LEGAL-ENTITY-UUID": currentLegalEntityUuid }),
      "X-HB-RELEASE": appConfig.BUILD_HASH,
      ...additionalHeaders,
    });
}

const TOO_MANY_REQUESTS_HTTP_CODE = 429;
const UNAUTHORIZED_HTTP_CODE = 401;
export const BAD_REQUEST_HTTP_CODE = 400;

export const OPEN_UPGRADE_MODAL = "OPEN_UPGRADE_MODAL";

/**
 * API client with automated token acquisition and response handling.
 * - Token is acquired on EVERY request via preProcessingRequestHooks
 * - MSAL handles caching internally, so minimal performance overhead
 * - Response hooks handle 401 (logout), 429 (rate limiting), and upgrade prompts
 */
export const baseClient = new LegalEntityRestrictedApiClient({
  auth,
  preProcessingResponseHooks: [
    async (resp): Promise<void> => {
      if (resp.status === UNAUTHORIZED_HTTP_CODE) {
        await msalInstance.logoutRedirect({
          onRedirectNavigate: () => false,
        });
      } else if (resp.status === TOO_MANY_REQUESTS_HTTP_CODE) {
        manageTooManyRequestError();
      }
      // in case of blacklisted build hash version, the proxy return 400 with error "UPGRADE_REQUIRED" in body
      if (resp.status === BAD_REQUEST_HTTP_CODE) {
        // Clone the response before reading it to avoid consuming the body stream
        const clonedResp = resp.clone();
        const responseJson = await clonedResp.json();
        if (responseJson.error === UPGRADE_REQUIRED) {
          window.postMessage({ type: OPEN_UPGRADE_MODAL }, "*");
        }
      }
    },
  ],
});

/**
 * Acquire fresh Microsoft token before request.
 * Pattern from dsit: get token on every request to guarantee freshness.
 * MSAL handles caching internally, so minimal performance overhead.
 */
const acquireTokenForRequest = async (): Promise<void> => {
  try {
    const authResult = await getMicrosoftToken();
    baseClient.updateToken(authResult.accessToken);
  } catch (error) {
    console.error("[API Client] Failed to acquire token for API request", error);
    // Don't throw - let the request proceed with existing token
    // 401 handler will trigger logout if needed
  }
};

/**
 * Wrapped API client with per-request token acquisition.
 * - Gets token on EVERY request to ensure it's always fresh
 * - MSAL handles caching internally, so minimal performance overhead
 */
export const client = {
  ...baseClient,
  get: async <T>(...args: Parameters<typeof baseClient.get<T>>): Promise<ApiResponse<T>> => {
    await acquireTokenForRequest();
    return baseClient.get<T>(...args);
  },
  post: async <T>(...args: Parameters<typeof baseClient.post<T>>): Promise<ApiResponse<T>> => {
    await acquireTokenForRequest();
    return baseClient.post<T>(...args);
  },
  put: async <T>(...args: Parameters<typeof baseClient.put<T>>): Promise<ApiResponse<T>> => {
    await acquireTokenForRequest();
    return baseClient.put<T>(...args);
  },
  httpDelete: async <T>(
    ...args: Parameters<typeof baseClient.httpDelete<T>>
  ): Promise<ApiResponse<T>> => {
    await acquireTokenForRequest();
    return baseClient.httpDelete<T>(...args);
  },
  postJson: async <T>(
    ...args: Parameters<typeof baseClient.postJson<T>>
  ): Promise<ApiResponse<T>> => {
    await acquireTokenForRequest();
    return baseClient.postJson<T>(...args);
  },
  putJson: async <T>(
    ...args: Parameters<typeof baseClient.putJson<T>>
  ): Promise<ApiResponse<T>> => {
    await acquireTokenForRequest();
    return baseClient.putJson<T>(...args);
  },
  deleteJson: async <T>(
    ...args: Parameters<typeof baseClient.deleteJson<T>>
  ): Promise<ApiResponse<T>> => {
    await acquireTokenForRequest();
    return baseClient.deleteJson<T>(...args);
  },
};
